import { useState } from "react";
import type { FC } from "react";
import Stack from "@mui/material/Stack";
import { Routes, Route, Navigate } from "react-router";
import { Drawer } from "../../../../shared/Drawer";
import { DynamicGrid } from "../../../../dynamicGrid";
import type { User } from "../../../../api/users.types";
import { PersonIcon2 } from "../../../../shared/icons";
import { useGetUsersGridView, useUpdateRoleHierarchy } from "../../../../api/users";
import { DeleteMenuItem } from "../DeleteMenuItem";
import type { TranslationKey } from "@privacy/translations";
import { RESPONSIBLE_ENTITIES_PATHS } from "../constants";
import { UserRolesSubdrawer } from "./UserRolesSubdrawer";
import { ResponsibleEntitesSubdrawer } from "./ResponsibleEntitesSubdrawer";
import { MainDrawerContent } from "./MainDrawerContent";
import { usePrivacyNavigate } from "../../../../shared/usePrivacyNavigate";
import { ActionMenu } from "../../../../shared/ActionMenu";
import { useScimEditingMode } from "../../shared/useScimEditingMode";

export const UserDrawer = () => {
  const userId = DynamicGrid.useSelectedRowId();
  const userGridViewQuery = useGetUsersGridView();
  const { navigate, paths } = usePrivacyNavigate();
  const updateRoleHierarchy = useUpdateRoleHierarchy();
  const { isScimEnabled } = useScimEditingMode();
  const [customDrawerWidth, setCustomDrawerWidth] = useState<string | undefined>(undefined);
  return (
    <DynamicGrid.Drawer width={customDrawerWidth}>
      {(() => {
        if (!userGridViewQuery.data) return <Drawer.Loading />;
        const user = userGridViewQuery.data.find(user => user.userId === userId);
        if (!user) {
          return <Navigate to={paths.usersGrid()} replace />;
        }

        return (
          <Stack sx={{ height: "100%" }}>
            <Drawer.Header
              title={`${user.firstName} ${user.lastName}`}
              onClose={() => navigate(paths.usersGrid())}
              status={updateRoleHierarchy.status}
              renderIcon={props => <PersonIcon2 {...props} />}
              topRightSlot={
                !isScimEnabled && (
                  <UserActionMenu
                    user={user}
                    onDeleted={() => navigate(paths.usersGrid())}
                    ariaLabelKey="GridDrawer.Menu.ButtonLabel"
                  />
                )
              }
            />
            <Routes>
              <Route index element={<MainDrawerContent user={user} />} />
              <Route
                path={RESPONSIBLE_ENTITIES_PATHS.registrationsWhereResponsible}
                element={
                  <ResponsibleEntitesSubdrawer user={user} responsibleEntitiesKey="registrationsWhereResponsible" />
                }
              />
              <Route
                path={RESPONSIBLE_ENTITIES_PATHS.processorRegistrationsWhereResponsible}
                element={
                  <ResponsibleEntitesSubdrawer
                    user={user}
                    responsibleEntitiesKey="processorRegistrationsWhereResponsible"
                  />
                }
              />
              <Route
                path={RESPONSIBLE_ENTITIES_PATHS.dpiasWhereResponsible}
                element={<ResponsibleEntitesSubdrawer user={user} responsibleEntitiesKey="dpiasWhereResponsible" />}
              />
              <Route
                path={RESPONSIBLE_ENTITIES_PATHS.systemsWhereResponsible}
                element={<ResponsibleEntitesSubdrawer user={user} responsibleEntitiesKey="systemsWhereResponsible" />}
              />

              <Route
                path="roles"
                element={
                  <UserRolesSubdrawer
                    updateDrawerWidth={setCustomDrawerWidth}
                    userId={user.userId}
                    updateRoleHierarchy={updateRoleHierarchy}
                  />
                }
              />

              <Route path="*" element={<Navigate to={paths.userById(user.userId)} />} />
            </Routes>
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

export const UserActionMenu: FC<{ user: User; onDeleted: () => void; ariaLabelKey?: TranslationKey }> = ({
  user,
  onDeleted,
  ariaLabelKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ActionMenu isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
      <DeleteMenuItem user={user} onClose={() => setIsOpen(false)} onDeleted={onDeleted} />
    </ActionMenu>
  );
};
